/**
 * Live socket connection state — roadmap Master Piece List row 1,
 * "Reconnection Issues".
 *
 * WHY THIS EXISTS
 *
 * Josh reported the MPL "having reconnection issues" on 2026-07-16. It has
 * never reproduced on our hardware, and reading the code turns up three
 * separate mechanisms that would each produce the same symptom — a grid that
 * quietly stops updating:
 *
 *   1. No reconnect handling on the live client at all. `EnhancedWebSocketStore`
 *      implements backoff, attempt caps and a user notification — and grepping
 *      the whole of src/ for it outside its own file returns NOTHING. It is
 *      never instantiated. The real socket is built in apiClient.ts, whose only
 *      connect/disconnect/connect_error handlers were wrapped in
 *      `if (debugMode)`: they logged, in debug builds, and did nothing else.
 *
 *   2. Handshake auth frozen at page load. The API authenticates a socket from
 *      its handshake query (channelSystem/handleConnections.ts →
 *      authOnSocketConnection.ts), and connections join the `authenticated`
 *      channel only on the `login` event (channels.ts). The client builds that
 *      query ONCE, from localStorage, at construction — and socket.io replays
 *      the original handshake on every reconnect. If the refresh token rotated
 *      meanwhile, the reconnect presents a stale one, re-auth fails, and the
 *      connection never rejoins `authenticated`. Real-time events stop while
 *      the socket reports itself connected.
 *
 *   3. `transports: ["websocket"]` with no polling fallback, so a proxied or
 *      captive-portal plant network fails outright rather than degrading.
 *
 * Those need different fixes, so this module deliberately does NOT fix
 * anything. It does two things that are worth having either way:
 *
 *   - reports what actually happens on Josh's tablets, via App Insights, so
 *     the fix is chosen from evidence rather than from these three guesses;
 *   - exposes the state so the UI can say "Reconnecting…" instead of showing
 *     stale data silently, which is most of what makes this maddening.
 *
 * Kept framework-free and store-free on purpose: apiClient is constructed
 * before the MobX root store exists, so this cannot depend on it.
 */

import { trackEvent } from "../appInsights";

export type ConnectionState = "connecting" | "connected" | "reconnecting" | "offline";

interface ConnectionSnapshot {
  state: ConnectionState;
  /** Consecutive failed attempts since the last successful connect. */
  attempts: number;
  /** ms since epoch of the last successful connect, or null if never. */
  connectedAt: number | null;
  /** socket.io's reason string for the most recent disconnect. */
  lastReason: string | null;
}

let snapshot: ConnectionSnapshot = {
  state: "connecting",
  attempts: 0,
  connectedAt: null,
  lastReason: null,
};

const listeners = new Set<() => void>();

/** Referentially stable between changes, so useSyncExternalStore won't loop. */
export const getConnectionSnapshot = (): ConnectionSnapshot => snapshot;

export const subscribeConnection = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const emit = (next: Partial<ConnectionSnapshot>) => {
  snapshot = { ...snapshot, ...next };
  listeners.forEach((l) => {
    try {
      l();
    } catch (err) {
      // One bad subscriber must not stop the others being told.
      console.warn("[connectionStatus] listener threw:", err);
    }
  });
};

/** Whole seconds the socket had been up, for correlating drops with sleep. */
const uptimeSeconds = (): string =>
  snapshot.connectedAt == null
    ? "unknown"
    : String(Math.round((Date.now() - snapshot.connectedAt) / 1000));

export type TrackFn = (name: string, properties?: { [key: string]: string }) => void;

/**
 * Attach lifecycle telemetry to a socket.io client.
 *
 * Everything here is UNCONDITIONAL — not gated on a debug flag. The whole
 * point is to capture what happens on a plant tablet in production, which is
 * the one place a debug-only handler can never see.
 *
 * The route is included because "which page was open when it dropped" is the
 * single most useful correlate for a report that names one screen.
 */
export const attachConnectionTelemetry = (
  socket: {
    on: (event: string, handler: (...args: unknown[]) => void) => void;
  },
  // Injectable so the state machine can be exercised without App Insights.
  // Production callers pass nothing.
  track: TrackFn = trackEvent
): void => {
  const route = () =>
    typeof window !== "undefined" && window.location
      ? window.location.pathname
      : "unknown";

  socket.on("connect", () => {
    const wasReconnect = snapshot.attempts > 0 || snapshot.connectedAt != null;
    track("socket.connect", {
      reconnect: String(wasReconnect),
      attempts: String(snapshot.attempts),
      route: route(),
    });
    emit({ state: "connected", attempts: 0, connectedAt: Date.now(), lastReason: null });
  });

  socket.on("disconnect", (...args: unknown[]) => {
    const reason = typeof args[0] === "string" ? args[0] : "unknown";
    track("socket.disconnect", {
      reason,
      uptimeSeconds: uptimeSeconds(),
      route: route(),
    });
    // "io server disconnect" and "io client disconnect" are deliberate and
    // socket.io will NOT retry them on its own; anything else it retries.
    const willRetry = reason !== "io server disconnect" && reason !== "io client disconnect";
    emit({ state: willRetry ? "reconnecting" : "offline", lastReason: reason });
  });

  socket.on("connect_error", (...args: unknown[]) => {
    const err = args[0] as { message?: string } | undefined;
    emit({ attempts: snapshot.attempts + 1 });
    track("socket.connect_error", {
      message: err?.message ? String(err.message).slice(0, 200) : "unknown",
      attempts: String(snapshot.attempts),
      route: route(),
    });
    if (snapshot.state === "connected") emit({ state: "reconnecting" });
  });

  // socket.io's own reconnect lifecycle. `reconnect_failed` fires once the
  // manager gives up entirely — after that nothing recovers without a reload,
  // which is exactly the state a user would describe as "it stopped working".
  socket.on("reconnect_attempt", (...args: unknown[]) => {
    const n = typeof args[0] === "number" ? args[0] : snapshot.attempts + 1;
    emit({ state: "reconnecting", attempts: n });
  });

  socket.on("reconnect_failed", () => {
    track("socket.reconnect_failed", {
      attempts: String(snapshot.attempts),
      route: route(),
    });
    emit({ state: "offline" });
  });
};
